import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  CheckCircle,
  Circle,
  ArrowRight,
  ArrowLeft,
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Sunrise,
  Layers,
  BarChart2,
  Users,
  Zap,
  ChevronRight,
  Star,
  X,
  Shield,
  GitBranch,
} from "react-feather";

// Base components
import BaseInput from "../../BaseComponents/BaseInput";
import BaseSelect from "../../BaseComponents/BaseSelect";

// Components
import TemplateBuilder from "../Hr/Payroll/PayrollTemplates/TemplateBuilder";
import StaffProfile from "../HR_Staff/staffProfile";
import AddEditLeaveTemplate from "../Hr/Setting/HR_Leave_Template/add_edit_leave_template";
import AddRole from "../role/addRole";
import AddEditShift from "../Hr/Setting/Shift_Setting/AddEditShift";
import OvertimePolicy from "../Hr/Setting/OvertimePolicy/OvertimePolicy";

// APIs
import { viewOrganizationApi, getOnboardingStatusApi } from "../../Api/OrganizationApi";
import { addCenterApi } from "../../Api/Center";
import { addPositionApi } from "../../Api/positionApi";
import { CalculationMethod } from "../../Api/AttendenceMode";
import { addHoliday } from "../../Api/HolidayTemplate";
import { listOfState, listOfDistrict, listOfCity, centerApi } from "../../Api/common";

// Constants
import {
  checkStatusCodeSuccess,
  getLocalItem,
  setLocalItem,
  handleArrayResponse,
} from "../../Constant/common";
import {
  OVERTIME_POLICY_TYPE,
  overtimePolicyTypeOptions,
} from "../../Constant/Organization/organization";
import { MonthCalculation } from "../../Constant/HR/Attendence_Mode";

import { SelectPlaceHolder, validationMessages } from "../../Constant/validation";
import { PlaceHolderFormat } from "../../Constant/requireMessage";

// ─── Indian holidays 2026 seed data ─────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();
const INDIAN_HOLIDAYS_SEED = [
  { holiday_name: "New Year's Day", holiday_date: `${CURRENT_YEAR}-01-01` },
  { holiday_name: "Republic Day", holiday_date: `${CURRENT_YEAR}-01-26` },
  { holiday_name: "Holi", holiday_date: `${CURRENT_YEAR}-03-14` },
  { holiday_name: "Eid ul-Fitr", holiday_date: `${CURRENT_YEAR}-03-31` },
  { holiday_name: "Good Friday", holiday_date: `${CURRENT_YEAR}-04-03` },
  { holiday_name: "Ram Navami", holiday_date: `${CURRENT_YEAR}-04-06` },
  { holiday_name: "Ambedkar Jayanti", holiday_date: `${CURRENT_YEAR}-04-14` },
  { holiday_name: "Maharashtra Day", holiday_date: `${CURRENT_YEAR}-05-01` },
  { holiday_name: "Eid ul-Adha", holiday_date: `${CURRENT_YEAR}-06-07` },
  { holiday_name: "Independence Day", holiday_date: `${CURRENT_YEAR}-08-15` },
  { holiday_name: "Janmashtami", holiday_date: `${CURRENT_YEAR}-08-16` },
  { holiday_name: "Gandhi Jayanti / Dussehra", holiday_date: `${CURRENT_YEAR}-10-02` },
  { holiday_name: "Diwali", holiday_date: `${CURRENT_YEAR}-10-20` },
  { holiday_name: "Diwali (Laxmi Puja)", holiday_date: `${CURRENT_YEAR}-10-21` },
  { holiday_name: "Guru Nanak Jayanti", holiday_date: `${CURRENT_YEAR}-11-05` },
  { holiday_name: "Christmas Day", holiday_date: `${CURRENT_YEAR}-12-25` },
];

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 1,
    key: "center",
    label: "Center",
    icon: MapPin,
    color: "#8b5cf6",
    desc: "Add your first center",
  },
  {
    id: 2,
    key: "role",
    label: "Role",
    icon: Shield,
    color: "#a855f7",
    desc: "Create your first role",
  },
  {
    id: 3,
    key: "position",
    label: "Position",
    icon: Briefcase,
    color: "#06b6d4",
    desc: "Create your first position",
  },
  {
    id: 5,
    key: "holidays",
    label: "Holiday Template",
    icon: Calendar,
    color: "#f59e0b",
    desc: "Default Indian holidays",
  },
  {
    id: 6,
    key: "leave_template",
    label: "Leave Template",
    icon: GitBranch,
    color: "#10b981",
    desc: "Define leave categories",
  },
  {
    id: 7,
    key: "shift",
    label: "Shift",
    icon: Sunrise,
    color: "#f97316",
    desc: "Define work shifts",
  },
  {
    id: 8,
    key: "month_calc",
    label: "Month Calculation",
    icon: BarChart2,
    color: "#ec4899",
    desc: "Salary calculation method",
  },
  {
    id: 4,
    key: "overtime_type",
    label: "Overtime Type",
    icon: DollarSign,
    color: "#10b981",
    desc: "Overtime compensation model",
  },
  {
    id: 9,
    key: "overtime_policy",
    label: "Overtime Policy",
    icon: Layers,
    color: "#14b8a6",
    desc: "Overtime rules & limits",
  },
  {
    id: 10,
    key: "payroll",
    label: "Payroll Template",
    icon: Zap,
    color: "#3b82f6",
    desc: "Salary structure template",
  },
  {
    id: 11,
    key: "staff",
    label: "Add Staff",
    icon: Users,
    color: "#f43f5e",
    desc: "Onboard your first employee",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const RadioCard = ({ opt, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
      padding: "14px 16px",
      borderRadius: 12,
      textAlign: "left",
      width: "100%",
      border: `1.5px solid ${active ? "rgba(99,102,241,0.4)" : "var(--glass-border)"}`,
      background: active ? "rgba(99,102,241,0.08)" : "var(--icon-btn-bg)",
      cursor: "pointer",
      transition: "all 0.15s",
    }}
  >
    <div
      style={{
        width: 18,
        height: 18,
        borderRadius: "50%",
        flexShrink: 0,
        marginTop: 2,
        border: `2px solid ${active ? "var(--brand-indigo)" : "var(--glass-border)"}`,
        background: active ? "var(--brand-indigo)" : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {active && <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff" }} />}
    </div>
    <div>
      <div
        style={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: active ? "var(--brand-indigo)" : "var(--text-primary)",
        }}
      >
        {opt.label}
      </div>
      {opt.labelSuffix && (
        <div
          style={{ fontSize: "0.75rem", color: "var(--text-muted)", marginTop: 3, lineHeight: 1.5 }}
        >
          {opt.labelSuffix}
        </div>
      )}
    </div>
  </button>
);

// ─── Step Progress Sidebar ───────────────────────────────────────────────────
const StepSidebar = ({ currentStep, completedSteps, steps = STEPS }) => {
  const activeStepsCount = completedSteps.filter((id) => steps.some((s) => s.id === id)).length;
  return (
    <div
      style={{
        width: 260,
        flexShrink: 0,
        background: "var(--glass-bg)",
        border: "1px solid var(--glass-border)",
        borderRadius: 20,
        padding: "24px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        position: "sticky",
        top: 0,
        alignSelf: "flex-start",
      }}
    >
      <div style={{ marginBottom: 20, paddingLeft: 8 }}>
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "var(--text-muted)",
            marginBottom: 4,
          }}
        >
          Setup Progress
        </div>
        <div
          style={{
            fontSize: "1.5rem",
            fontWeight: 800,
            background: "linear-gradient(135deg,#6366f1,#8b5cf6,#06b6d4)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {activeStepsCount}/{steps.length}
        </div>
        {/* Progress bar */}
        <div
          style={{
            marginTop: 8,
            height: 4,
            borderRadius: 999,
            background: "var(--glass-border)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              borderRadius: 999,
              background: "linear-gradient(90deg,#6366f1,#8b5cf6)",
              width: `${steps.length ? (activeStepsCount / steps.length) * 100 : 0}%`,
              transition: "width 0.4s ease",
            }}
          />
        </div>
      </div>

      {steps.map((step) => {
        const done = completedSteps.includes(step.id);
        const active = currentStep === step.id;
        const Icon = step.icon;
        return (
          <div
            key={step.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 10px",
              borderRadius: 10,
              background: active ? `${step.color}12` : "transparent",
              border: `1px solid ${active ? `${step.color}30` : "transparent"}`,
              transition: "all 0.2s",
            }}
          >
            {/* Step indicator */}
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                flexShrink: 0,
                background: done
                  ? `${step.color}18`
                  : active
                    ? `${step.color}18`
                    : "var(--glass-bg)",
                border: `1.5px solid ${done ? step.color : active ? step.color : "var(--glass-border)"}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s",
              }}
            >
              {done ? (
                <CheckCircle size={14} strokeWidth={2.5} style={{ color: step.color }} />
              ) : active ? (
                <Icon size={13} strokeWidth={2} style={{ color: step.color }} />
              ) : (
                <Circle
                  size={12}
                  strokeWidth={1.5}
                  style={{ color: "var(--text-muted)", opacity: 0.4 }}
                />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: "0.8125rem",
                  fontWeight: active || done ? 700 : 500,
                  color: active ? step.color : done ? "var(--text-primary)" : "var(--text-muted)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {step.label}
              </div>
              {active && (
                <div style={{ fontSize: "0.6875rem", color: "var(--text-muted)", marginTop: 1 }}>
                  {step.desc}
                </div>
              )}
            </div>
            {done && (
              <span
                style={{
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  padding: "2px 6px",
                  borderRadius: 999,
                  background: `${step.color}15`,
                  color: step.color,
                  border: `1px solid ${step.color}30`,
                }}
              >
                Done
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Step Header ─────────────────────────────────────────────────────────────
const StepHeader = ({ step, isSkippable, onSkip, steps = STEPS }) => {
  const Icon = step.icon;
  const currentIdx = steps.findIndex((s) => s.id === step.id);
  const displayStepNumber = currentIdx !== -1 ? currentIdx + 1 : step.id;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: 24,
        paddingBottom: 20,
        borderBottom: "1px solid var(--glass-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 14,
            flexShrink: 0,
            background: `${step.color}15`,
            border: `1.5px solid ${step.color}30`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={24} strokeWidth={1.75} style={{ color: step.color }} />
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
            <span
              style={{
                fontSize: "0.6875rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: step.color,
              }}
            >
              Step {displayStepNumber} of {steps.length}
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: "1.25rem",
              fontWeight: 800,
              color: "var(--text-primary)",
            }}
          >
            {step.label}
          </h2>
          <p style={{ margin: "2px 0 0", fontSize: "0.8125rem", color: "var(--text-muted)" }}>
            {step.desc}
          </p>
        </div>
      </div>
      {isSkippable && (
        <button
          type="button"
          onClick={onSkip}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "6px 12px",
            borderRadius: 8,
            background: "var(--icon-btn-bg)",
            border: "1px solid var(--glass-border)",
            color: "var(--text-muted)",
            fontSize: "0.8125rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.15s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--text-primary)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--text-muted)";
          }}
        >
          <X size={13} strokeWidth={2.5} /> Skip
        </button>
      )}
    </div>
  );
};

// ─── Main Onboarding Component ───────────────────────────────────────────────
const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [btnLoader, setBtnLoader] = useState(false);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  const [allowedStepKeys, setAllowedStepKeys] = useState([]);

  const filteredSteps = useMemo(() => {
    if (!onboardingLoaded) return STEPS;
    const completedKeys = completedSteps
      .map((id) => STEPS.find((s) => s.id === id)?.key)
      .filter(Boolean);
    const currentKey = STEPS.find((s) => s.id === currentStep)?.key;
    const activeKeys = new Set([...completedKeys, ...allowedStepKeys, currentKey].filter(Boolean));
    if (activeKeys.size === 0) return STEPS;
    return STEPS.filter((step) => activeKeys.has(step.key));
  }, [onboardingLoaded, completedSteps, allowedStepKeys, currentStep]);

  // Persisted IDs from completed steps
  const [orgId] = useState(() =>
    getLocalItem("onboarding_org_id")
      ? parseInt(getLocalItem("onboarding_org_id"))
      : getLocalItem("organizationId")
        ? parseInt(getLocalItem("organizationId"))
        : null,
  );
  const [centerId, setCenterId] = useState(() =>
    getLocalItem("onboarding_center_id") ? parseInt(getLocalItem("onboarding_center_id")) : null,
  );
  const [orgOvertimeType, setOrgOvertimeType] = useState(OVERTIME_POLICY_TYPE.PAYMENT);

  // Location dropdowns for Center step
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  // Holiday selection state
  const [selectedHolidays, setSelectedHolidays] = useState(INDIAN_HOLIDAYS_SEED.map((_, i) => i));

  // Attendance & month calc local state (for radio cards)
  const [monthCalcMode, setMonthCalcMode] = useState("Calender Month");

  useEffect(() => {
    document.title = "Setup — HR Fusions";

    if (orgId) {
      // Fetch onboarding status
      getOnboardingStatusApi(orgId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const data = resp?.data;
            if (data) {
              const allKeys = [...(data.completedSteps || []), ...(data.pendingSteps || [])];
              setAllowedStepKeys(allKeys);

              const completedStepIds = [];
              if (data.completedSteps?.includes("center")) completedStepIds.push(1);
              if (data.completedSteps?.includes("role")) completedStepIds.push(2);
              if (data.completedSteps?.includes("position")) completedStepIds.push(3);
              if (data.completedSteps?.includes("overtime_type")) completedStepIds.push(4);
              if (data.completedSteps?.includes("holidays")) completedStepIds.push(5);
              if (data.completedSteps?.includes("leave_template")) completedStepIds.push(6);
              if (data.completedSteps?.includes("shift")) completedStepIds.push(7);
              if (data.completedSteps?.includes("month_calc")) completedStepIds.push(8);
              if (data.completedSteps?.includes("overtime_policy")) completedStepIds.push(9);
              if (data.completedSteps?.includes("payroll")) completedStepIds.push(10);
              if (data.completedSteps?.includes("staff")) completedStepIds.push(11);

              setCompletedSteps(completedStepIds);
              const firstIncomplete = STEPS.find((s) => !completedStepIds.includes(s.id));
              if (firstIncomplete) {
                setCurrentStep(firstIncomplete.id);
              } else if (data.currentStep) {
                setCurrentStep(data.currentStep);
              }
              if (data.completed) {
                navigate("/HrDashboard", { replace: true });
              }
            }
          }
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error("Failed to load onboarding status", err);
        })
        .finally(() => {
          setOnboardingLoaded(true);
        });

      // Fetch organization details (for overtime policy type)
      viewOrganizationApi(orgId)
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            const org = resp?.data;
            if (org?.overtime_policy_type) {
              setOrgOvertimeType(org.overtime_policy_type);
            }
          }
        })
        .catch(() => {});

      // Fetch centers if centerId is not set
      if (!centerId) {
        centerApi()
          .then((resp) => {
            if (checkStatusCodeSuccess(resp?.statusCode)) {
              const list = resp?.data ?? [];
              if (Array.isArray(list) && list.length > 0) {
                const firstCenterId = list[0].id;
                setCenterId(firstCenterId);
                setLocalItem("onboarding_center_id", firstCenterId);
              }
            }
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch centers in onboarding", err);
          });
      }
    } else {
      setOnboardingLoaded(true);
    }

    listOfState({})
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setStates(resp?.data?.map((s) => ({ value: s.id, label: s.state_name })) || []);
        }
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, navigate]);

  const markDone = (stepId) => {
    setCompletedSteps((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
  };

  const goNext = () => {
    const currentIndex = filteredSteps.findIndex((s) => s.id === currentStep);
    if (currentIndex !== -1 && currentIndex < filteredSteps.length - 1) {
      setCurrentStep(filteredSteps[currentIndex + 1].id);
    }
  };

  const goBack = () => {
    const currentIndex = filteredSteps.findIndex((s) => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(filteredSteps[currentIndex - 1].id);
    }
  };

  const skipStep = () => {
    markDone(currentStep);
    goNext();
  };

  const currentStepDef = useMemo(() => {
    return STEPS.find((s) => s.id === currentStep) || STEPS[0];
  }, [currentStep]);

  // ── STEP 2: Center ────────────────────────────────────────────────────────
  const centerForm = useFormik({
    initialValues: {
      center_name: "",
      location: "",
      address: "",
      pin_code: "",
      state_name: null,
      district: null,
      city: null,
      description: "",
    },
    validationSchema: Yup.object({
      center_name: Yup.string().required(validationMessages.required("Center Name")),
      location: Yup.string().required(validationMessages.required("Location")),
      address: Yup.string().required(validationMessages.required("Address")),
      pin_code: Yup.string()
        .required(validationMessages.required("Pin Code"))
        .matches(/^\d{6}$/, "Pin Code must be 6 digits"),
      state_name: Yup.string().required(validationMessages.required("State")).nullable(),
      district: Yup.string().required(validationMessages.required("District")).nullable(),
      city: Yup.string().required(validationMessages.required("City")).nullable(),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      const resolvedOrgId = orgId || getLocalItem("organizationId");
      addCenterApi({
        organizationId: parseInt(resolvedOrgId),
        center_name: values.center_name,
        location: values.location,
        address: values.address,
        pin_code: `${values.pin_code}`,
        state_id: values.state_name,
        district_id: values.district,
        city_id: values.city,
        description: values.description,
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            // Response data is the center ID directly (e.g. data: 10)
            const newCenterId = resp?.data?.id ?? resp?.data;
            if (newCenterId) {
              const parsed = parseInt(newCenterId);
              setCenterId(parsed);
              setLocalItem("onboarding_center_id", parsed);
            }
            toast.success(resp?.message || "Center created!");
            markDone(1);
            goNext();
          } else {
            toast.error(resp?.message);
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  // ── STEP 4: Position Formik ────────────────────────────────────────────────
  const positionForm = useFormik({
    initialValues: { position_name: "" },
    validationSchema: Yup.object({
      position_name: Yup.string().required(validationMessages.required("Position Name")),
    }),
    onSubmit: (values) => {
      setBtnLoader(true);
      const resolvedOrgId = orgId || getLocalItem("organizationId");
      addPositionApi({
        position_name: values.position_name,
        organizationId: resolvedOrgId ? parseInt(resolvedOrgId) : null,
      })
        .then((resp) => {
          if (checkStatusCodeSuccess(resp?.statusCode)) {
            toast.success(resp?.message || "Position created successfully!");
            markDone(3);
            goNext();
          } else {
            toast.error(resp?.message || "Failed to create position");
          }
        })
        .catch((err) => handleArrayResponse(err))
        .finally(() => setBtnLoader(false));
    },
  });

  const fetchDistrict = useCallback((stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    listOfDistrict({ condition: { state_id: stateId } })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setDistricts(resp?.data?.map((d) => ({ value: d.id, label: d.district_name })) || []);
        }
      })
      .catch(() => setDistricts([]));
  }, []);

  const fetchCity = useCallback((districtId) => {
    if (!districtId) {
      setCities([]);
      return;
    }
    listOfCity({ condition: { district_id: districtId } })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setCities(resp?.data?.map((c) => ({ value: c.id, label: c.city_name })) || []);
        }
      })
      .catch(() => setCities([]));
  }, []);

  // ── STEP 5: Overtime Type (saved in org, already done in step 1) ──────────
  // This step is informational — shows what was selected and lets user confirm
  const handleOvertimeTypeConfirm = () => {
    markDone(4);
    goNext();
  };

  // ── STEP 5: Holiday Template ──────────────────────────────────────────────
  const handleHolidaySubmit = () => {
    const chosen = INDIAN_HOLIDAYS_SEED.filter((_, i) => selectedHolidays.includes(i));
    if (chosen.length === 0) {
      toast.error("Select at least one holiday.");
      return;
    }
    setBtnLoader(true);
    const resolvedOrgId = orgId || getLocalItem("organizationId");
    const resolvedCenterId = centerId || getLocalItem("onboarding_center_id");
    addHoliday({
      template_name: `Default Holidays ${CURRENT_YEAR}`,
      from_date: `${CURRENT_YEAR}-01`,
      to_date: `${CURRENT_YEAR}-12`,
      holidays: chosen.map(({ holiday_name, holiday_date }) => ({
        holiday_name,
        holiday_date,
      })),
      ...(resolvedOrgId ? { organization_id: parseInt(resolvedOrgId) } : {}),
      ...(resolvedCenterId ? { center_id: parseInt(resolvedCenterId) } : {}),
    })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message || "Holiday template created!");
          markDone(5);
          goNext();
        } else if (
          resp?.statusCode === 409 ||
          resp?.message === "Holiday template is already exist" ||
          resp?.message?.toLowerCase().includes("already exist")
        ) {
          toast.info(resp?.message || "Holiday template already exists.");
          markDone(5);
          goNext();
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        const errorMsg = err?.response?.data?.message || err?.message || "";
        const statusCode = err?.response?.status || err?.response?.data?.statusCode;
        if (
          statusCode === 409 ||
          errorMsg === "Holiday template is already exist" ||
          (typeof errorMsg === "string" && errorMsg.toLowerCase().includes("already exist"))
        ) {
          toast.info(errorMsg || "Holiday template already exists.");
          markDone(5);
          goNext();
        } else {
          handleArrayResponse(err);
        }
      })
      .finally(() => setBtnLoader(false));
  };

  const toggleHoliday = (idx) => {
    setSelectedHolidays((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx],
    );
  };

  // ── STEP 6: Shift Timing & Overtime Formik definitions removed (using inline components AddEditShift & OvertimePolicy)

  // ── STEP 8: Month Calculation ─────────────────────────────────────────────
  const handleMonthCalcSubmit = () => {
    setBtnLoader(true);
    CalculationMethod({ salary_calculation_method: monthCalcMode })
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          toast.success(resp?.message || "Month calculation saved!");
          markDone(8);
          goNext();
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => handleArrayResponse(err))
      .finally(() => setBtnLoader(false));
  };

  // ── Finish ────────────────────────────────────────────────────────────────
  const handleFinish = () => {
    // Clean up temporary onboarding keys
    localStorage.removeItem("onboarding_org_id");
    localStorage.removeItem("onboarding_center_id");
    toast.success("Setup complete! Welcome to HR Fusions.");
    navigate("/HrDashboard");
  };

  // ── Render step content ───────────────────────────────────────────────────
  const renderStepContent = () => {
    switch (currentStep) {
      // ── Step 1: Center ────────────────────────────────────────────────────
      case 1:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(1) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Center created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your center is configured. Click <strong>Next</strong> to define roles.
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={centerForm.handleSubmit}>
                <div className="onboarding-grid">
                  <BaseInput
                    name="center_name"
                    label="Center Name"
                    placeholder={PlaceHolderFormat("Center Name")}
                    value={centerForm.values.center_name}
                    handleChange={centerForm.handleChange}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.center_name}
                    error={centerForm.errors.center_name}
                    required
                  />
                  <BaseInput
                    name="location"
                    label="Location"
                    placeholder={PlaceHolderFormat("Location")}
                    value={centerForm.values.location}
                    handleChange={centerForm.handleChange}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.location}
                    error={centerForm.errors.location}
                    required
                  />
                  <BaseSelect
                    name="state_name"
                    label="State"
                    options={states}
                    placeholder={SelectPlaceHolder("State")}
                    value={centerForm.values.state_name}
                    handleChange={(field, val) => {
                      centerForm.setFieldValue(field, val);
                      centerForm.setFieldValue("district", null);
                      centerForm.setFieldValue("city", null);
                      fetchDistrict(val);
                      setCities([]);
                    }}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.state_name}
                    error={centerForm.errors.state_name}
                    required
                  />
                  <BaseSelect
                    name="district"
                    label="District"
                    options={districts}
                    placeholder={SelectPlaceHolder("District")}
                    value={centerForm.values.district}
                    handleChange={(field, val) => {
                      centerForm.setFieldValue(field, val);
                      centerForm.setFieldValue("city", null);
                      fetchCity(val);
                    }}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.district}
                    error={centerForm.errors.district}
                    required
                  />
                  <BaseSelect
                    name="city"
                    label="City"
                    options={cities}
                    placeholder={SelectPlaceHolder("City")}
                    value={centerForm.values.city}
                    handleChange={(field, val) => centerForm.setFieldValue(field, val)}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.city}
                    error={centerForm.errors.city}
                    required
                  />
                  <BaseInput
                    name="pin_code"
                    label="Pin Code"
                    type="number"
                    placeholder={PlaceHolderFormat("Pin Code")}
                    value={centerForm.values.pin_code}
                    handleChange={centerForm.handleChange}
                    handleBlur={centerForm.handleBlur}
                    touched={centerForm.touched.pin_code}
                    error={centerForm.errors.pin_code}
                    required
                  />
                  <div style={{ gridColumn: "1 / -1" }}>
                    <BaseInput
                      name="address"
                      label="Address"
                      type="textarea"
                      placeholder={PlaceHolderFormat("Address")}
                      value={centerForm.values.address}
                      handleChange={centerForm.handleChange}
                      handleBlur={centerForm.handleBlur}
                      touched={centerForm.touched.address}
                      error={centerForm.errors.address}
                      required
                    />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <BaseInput
                      name="description"
                      label="Description (optional)"
                      type="textarea"
                      placeholder={PlaceHolderFormat("Description")}
                      value={centerForm.values.description}
                      handleChange={centerForm.handleChange}
                      handleBlur={centerForm.handleBlur}
                      touched={centerForm.touched.description}
                      error={centerForm.errors.description}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        );

      // ── Step 2: Role ──────────────────────────────────────────────────────
      case 2:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(2) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Role created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your role is configured. Click <strong>Next</strong> to define positions.
                  </div>
                </div>
              </div>
            ) : (
              <AddRole
                isOnboarding={true}
                onSuccess={() => {
                  markDone(2);
                  goNext();
                }}
              />
            )}
          </div>
        );

      // ── Step 3: Position ──────────────────────────────────────────────────
      case 3:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(3) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Position created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your first position is set. Click <strong>Next</strong> to configure overtime.
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={positionForm.handleSubmit}>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <BaseInput
                    name="position_name"
                    label="Position Name"
                    placeholder="e.g. Senior Software Engineer"
                    value={positionForm.values.position_name}
                    handleChange={positionForm.handleChange}
                    handleBlur={positionForm.handleBlur}
                    touched={positionForm.touched.position_name}
                    error={positionForm.errors.position_name}
                    required
                  />
                </div>
              </form>
            )}
          </div>
        );

      // ── Step 4: Overtime Type (confirm) ───────────────────────────────────
      case 4: {
        const selectedOpt = overtimePolicyTypeOptions.find((o) => o.value === orgOvertimeType);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
              style={{
                padding: "20px",
                borderRadius: 16,
                background: `${selectedOpt?.color || "#6366f1"}08`,
                border: `1.5px solid ${selectedOpt?.color || "#6366f1"}25`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: `${selectedOpt?.color}18`,
                    border: `1.5px solid ${selectedOpt?.color}30`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {selectedOpt && (
                    <selectedOpt.Icon
                      size={24}
                      strokeWidth={1.75}
                      style={{ color: selectedOpt.color }}
                    />
                  )}
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1.125rem", color: selectedOpt?.color }}>
                    {selectedOpt?.label}
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    {selectedOpt?.description}
                  </div>
                </div>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  lineHeight: 1.6,
                }}
              >
                This was set when you created your organisation. You can change it later from the
                Organisation settings.
              </p>
            </div>
            <div
              style={{
                padding: "14px 16px",
                borderRadius: 12,
                background: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.15)",
                fontSize: "0.8125rem",
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              <strong style={{ color: "var(--text-primary)" }}>What this means:</strong>{" "}
              {orgOvertimeType === OVERTIME_POLICY_TYPE.PAYMENT
                ? "Staff will receive monetary compensation for overtime hours. You will configure multipliers (e.g. 1.5× for weekends) in the Overtime Policy step."
                : "Staff will earn compensatory leave credits for overtime hours. You will configure the comp-off rate in the Overtime Policy step."}
            </div>
          </div>
        );
      }

      // ── Step 5: Holiday Template ──────────────────────────────────────────
      case 5:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(5) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Holiday template created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your holiday templates are configured. Click <strong>Next</strong> to define
                    leave templates.
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 14px",
                    borderRadius: 10,
                    background: "rgba(245,158,11,0.06)",
                    border: "1px solid rgba(245,158,11,0.15)",
                  }}
                >
                  <span style={{ fontSize: "0.8125rem", color: "var(--text-muted)" }}>
                    <strong style={{ color: "var(--text-primary)" }}>
                      {selectedHolidays.length}
                    </strong>{" "}
                    of {INDIAN_HOLIDAYS_SEED.length} holidays selected for {CURRENT_YEAR}
                  </span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => setSelectedHolidays(INDIAN_HOLIDAYS_SEED.map((_, i) => i))}
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#10b981",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedHolidays([])}
                      style={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#f43f5e",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    maxHeight: 380,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  {INDIAN_HOLIDAYS_SEED.map((h, idx) => {
                    const checked = selectedHolidays.includes(idx);
                    const typeColor =
                      h.holiday_type === "National"
                        ? "#6366f1"
                        : h.holiday_type === "Festival"
                          ? "#f59e0b"
                          : "#10b981";
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => toggleHoliday(idx)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "10px 14px",
                          borderRadius: 10,
                          textAlign: "left",
                          width: "100%",
                          border: `1.5px solid ${checked ? "rgba(99,102,241,0.3)" : "var(--glass-border)"}`,
                          background: checked ? "rgba(99,102,241,0.06)" : "var(--glass-bg)",
                          cursor: "pointer",
                          transition: "all 0.15s",
                        }}
                      >
                        {/* Checkbox */}
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: 5,
                            flexShrink: 0,
                            border: `2px solid ${checked ? "var(--brand-indigo)" : "var(--glass-border)"}`,
                            background: checked ? "var(--brand-indigo)" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {checked && (
                            <CheckCircle size={11} strokeWidth={3} style={{ color: "#fff" }} />
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "0.875rem",
                              color: "var(--text-primary)",
                            }}
                          >
                            {h.holiday_name}
                          </div>
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "var(--text-muted)",
                              marginTop: 1,
                            }}
                          >
                            {new Date(h.holiday_date).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "long",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: "0.6875rem",
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 999,
                            background: `${typeColor}15`,
                            color: typeColor,
                            border: `1px solid ${typeColor}25`,
                          }}
                        >
                          {h.holiday_type}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      // ── Step 6: Leave Template ────────────────────────────────────────────
      case 6:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(6) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Leave template created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your leave structure is configured. Click <strong>Next</strong> to define
                    shifts.
                  </div>
                </div>
              </div>
            ) : (
              <AddEditLeaveTemplate
                isOnboarding={true}
                onSuccess={() => {
                  markDone(6);
                  goNext();
                }}
              />
            )}
          </div>
        );

      // ── Step 7: Shift ─────────────────────────────────────────────────────
      case 7:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(7) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Shift created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your work shifts are set. Click <strong>Next</strong> to configure salary
                    calculations.
                  </div>
                </div>
              </div>
            ) : (
              <AddEditShift
                isOnboarding={true}
                onSuccess={() => {
                  markDone(7);
                  goNext();
                }}
              />
            )}
          </div>
        );

      // ── Step 8: Month Calculation ─────────────────────────────────────────
      case 8:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <p style={{ margin: "0 0 8px", fontSize: "0.875rem", color: "var(--text-muted)" }}>
              How do you calculate monthly salary?
            </p>
            {MonthCalculation.map((opt) => (
              <RadioCard
                key={opt.id}
                opt={opt}
                active={monthCalcMode === opt.value}
                onClick={() => setMonthCalcMode(opt.value)}
              />
            ))}
          </div>
        );

      // ── Step 9: Overtime Policy ───────────────────────────────────────────
      case 9:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(9) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Overtime policy created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your overtime rules are configured. Click <strong>Next</strong> to create
                    payroll templates.
                  </div>
                </div>
              </div>
            ) : (
              <OvertimePolicy
                isOnboarding={true}
                onSuccess={() => {
                  markDone(9);
                  goNext();
                }}
              />
            )}
          </div>
        );

      // ── Step 10: Payroll Template ────────────────────────────────────────
      case 10:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Status banner */}
            {completedSteps.includes(10) ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "14px 18px",
                  borderRadius: 14,
                  background: "rgba(16,185,129,0.08)",
                  border: "1.5px solid rgba(16,185,129,0.25)",
                }}
              >
                <CheckCircle
                  size={22}
                  strokeWidth={2}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "0.9375rem", color: "#10b981" }}>
                    Payroll template created!
                  </div>
                  <div style={{ fontSize: "0.8125rem", color: "var(--text-muted)", marginTop: 2 }}>
                    Your salary structure is ready. Click <strong>Next</strong> to add your first
                    staff member.
                  </div>
                </div>
              </div>
            ) : (
              <TemplateBuilder
                isOnboarding={true}
                onSuccess={() => {
                  markDone(10);
                  goNext();
                }}
              />
            )}
            {completedSteps.includes(10) && (
              <div
                style={{
                  padding: "12px 16px",
                  borderRadius: 10,
                  background: "rgba(99,102,241,0.06)",
                  border: "1px solid rgba(99,102,241,0.15)",
                  fontSize: "0.8125rem",
                  color: "var(--text-muted)",
                  lineHeight: 1.6,
                }}
              >
                <strong style={{ color: "var(--text-primary)" }}>Tip:</strong> You can create
                multiple templates for different roles (e.g. Senior Engineer, Junior Developer). Add
                more from <strong>HR → Payroll</strong> anytime.
              </div>
            )}
          </div>
        );

      // ── Step 11: Add Staff ────────────────────────────────────────────────
      case 11:
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {completedSteps.includes(11) ? (
              /* ── All done — celebration screen ── */
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div
                  style={{
                    padding: "28px 24px",
                    borderRadius: 20,
                    textAlign: "center",
                    background: "linear-gradient(135deg,rgba(16,185,129,0.1),rgba(6,182,212,0.06))",
                    border: "1.5px solid rgba(16,185,129,0.25)",
                  }}
                >
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      margin: "0 auto 16px",
                      background: "linear-gradient(135deg,#10b981,#06b6d4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 32px rgba(16,185,129,0.35)",
                    }}
                  >
                    <CheckCircle size={36} strokeWidth={2} style={{ color: "#fff" }} />
                  </div>
                  <h3
                    style={{
                      margin: "0 0 8px",
                      fontSize: "1.375rem",
                      fontWeight: 800,
                      color: "var(--text-primary)",
                    }}
                  >
                    You&apos;re all set! 🎉
                  </h3>
                  <p
                    style={{
                      margin: "0 0 20px",
                      fontSize: "0.9375rem",
                      color: "var(--text-muted)",
                      lineHeight: 1.6,
                    }}
                  >
                    Your workspace is fully configured. HR Fusions is ready to use.
                  </p>
                  {/* Summary chips */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                      justifyContent: "center",
                      marginBottom: 24,
                    }}
                  >
                    {STEPS.map((s) => {
                      const done = completedSteps.includes(s.id);
                      return (
                        <span
                          key={s.id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            padding: "4px 12px",
                            borderRadius: 999,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            background: done ? `${s.color}15` : "var(--glass-bg)",
                            color: done ? s.color : "var(--text-muted)",
                            border: `1px solid ${done ? s.color + "30" : "var(--glass-border)"}`,
                          }}
                        >
                          {done ? (
                            <CheckCircle size={11} strokeWidth={2.5} />
                          ) : (
                            <Circle size={11} strokeWidth={1.5} />
                          )}
                          {s.label}
                        </span>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={handleFinish}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "12px 32px",
                      borderRadius: 12,
                      background: "linear-gradient(135deg,#10b981,#06b6d4)",
                      border: "none",
                      color: "#fff",
                      fontSize: "1rem",
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow: "0 6px 24px rgba(16,185,129,0.4)",
                      transition: "transform 0.15s, box-shadow 0.15s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = "0 10px 32px rgba(16,185,129,0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 6px 24px rgba(16,185,129,0.4)";
                    }}
                  >
                    Go to Dashboard <ArrowRight size={18} strokeWidth={2.5} />
                  </button>
                </div>
              </div>
            ) : (
              <StaffProfile
                isOnboarding={true}
                onSuccess={() => {
                  markDone(11);
                }}
              />
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // ── Action button logic ───────────────────────────────────────────────────
  const handlePrimaryAction = () => {
    switch (currentStep) {
      case 1:
        if (completedSteps.includes(1)) {
          goNext();
        } else {
          centerForm.handleSubmit();
        }
        break;
      case 2:
        if (completedSteps.includes(2)) {
          goNext();
        }
        break;
      case 3:
        if (completedSteps.includes(3)) {
          goNext();
        } else {
          positionForm.handleSubmit();
        }
        break;
      case 4:
        handleOvertimeTypeConfirm();
        break;
      case 5:
        if (completedSteps.includes(5)) {
          goNext();
        } else {
          handleHolidaySubmit();
        }
        break;
      case 6:
        if (completedSteps.includes(6)) {
          goNext();
        }
        break;
      case 7:
        if (completedSteps.includes(7)) {
          goNext();
        }
        break;
      case 8:
        handleMonthCalcSubmit();
        break;
      case 9:
        if (completedSteps.includes(9)) {
          goNext();
        }
        break;
      case 10:
        if (completedSteps.includes(10)) {
          goNext();
        } else {
          window.open("/payrollTemplates/new", "_blank");
        }
        break;
      case 11:
        if (completedSteps.includes(11)) {
          handleFinish();
        } else {
          window.open("/staffProfile", "_blank");
        }
        break;
      default:
        goNext();
    }
  };

  const primaryLabel = () => {
    if (currentStep === 11) {
      return completedSteps.includes(11) ? "Go to Dashboard" : "Mark as Done";
    }
    if (currentStep === 10) {
      return completedSteps.includes(10) ? "Next" : "Open Template Builder";
    }
    if (completedSteps.includes(currentStep)) return "Next";
    return "Save & Continue";
  };

  const isSkippable = currentStep >= 5; // Steps 5-11 can be skipped

  if (!onboardingLoaded) {
    const saved = localStorage.getItem("mix_background_layout");
    const isDark = saved === "dark-only" || saved === "dark-sidebar";
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: isDark ? "#090a0f" : "#fafdff",
          gap: 16,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            border: `3px solid ${isDark ? "rgba(99,102,241,0.15)" : "rgba(99,102,241,0.1)"}`,
            borderTopColor: "var(--brand-indigo, #6366f1)",
            animation: "spin 1s linear infinite",
          }}
        />
        <span
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: isDark ? "#94a3b8" : "#475569",
            letterSpacing: "0.05em",
          }}
        >
          Loading Setup Status...
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div
      className="animate-fade-in"
      style={{
        minHeight: "100vh",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      {/* ── Page Header ── */}
      <div
        className="animate-slide-down"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: "linear-gradient(135deg,rgba(99,102,241,0.15),rgba(139,92,246,0.1))",
              border: "1.5px solid rgba(99,102,241,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Star size={22} strokeWidth={1.75} style={{ color: "var(--brand-indigo)" }} />
          </div>
          <div>
            <h1
              style={{
                margin: 0,
                fontSize: "1.5rem",
                fontWeight: 800,
                color: "var(--text-primary)",
              }}
            >
              Welcome to HR Fusions
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: "0.875rem", color: "var(--text-muted)" }}>
              Let&apos;s set up your workspace in a few quick steps
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/HrDashboard")}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 10,
            background: "var(--icon-btn-bg)",
            border: "1px solid var(--glass-border)",
            color: "var(--text-muted)",
            fontSize: "0.8125rem",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Skip Setup <ChevronRight size={14} strokeWidth={2.5} />
        </button>
      </div>

      {/* ── Main Layout ── */}
      <style>{`
        .onboarding-main-layout {
          display: flex;
          gap: 24px;
          align-items: flex-start;
        }
        .onboarding-sidebar {
          width: 260px;
          flex-shrink: 0;
        }
        .onboarding-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 16px;
        }
        @media (max-width: 1199px) {
          .onboarding-main-layout {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .onboarding-sidebar {
            width: 100% !important;
          }
          .onboarding-sidebar > div {
            width: 100% !important;
            position: static !important;
          }
        }
        @media (max-width: 768px) {
          .onboarding-step-card {
            padding: 16px !important;
            border-radius: 14px !important;
          }
          .onboarding-grid {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }
          .onboarding-step-footer {
            flex-direction: column-reverse !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .onboarding-step-footer > button {
            width: 100% !important;
            justify-content: center !important;
          }
          .onboarding-step-footer > div {
            justify-content: center !important;
          }
        }
      `}</style>
      <div
        className="onboarding-main-layout"
        style={{ display: "flex", gap: 24, alignItems: "flex-start" }}
      >
        {/* Sidebar */}
        <div className="onboarding-sidebar animate-slide-up" style={{ animationDelay: "60ms" }}>
          <StepSidebar
            currentStep={currentStep}
            completedSteps={completedSteps}
            steps={filteredSteps}
          />
        </div>

        {/* Step Card */}
        <div
          className="onboarding-step-card animate-slide-up"
          style={{
            flex: 1,
            minWidth: 0,
            background: "var(--glass-bg)",
            border: "1px solid var(--glass-border)",
            borderRadius: 20,
            padding: "28px",
            animationDelay: "120ms",
          }}
        >
          {/* Step Header */}
          <StepHeader
            step={currentStepDef}
            isSkippable={isSkippable && !completedSteps.includes(currentStep)}
            onSkip={skipStep}
            steps={filteredSteps}
          />

          {/* Step Content */}
          <div style={{ minHeight: 200 }}>{renderStepContent()}</div>

          {/* ── Navigation Footer ── */}
          <div
            className="onboarding-step-footer"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 28,
              paddingTop: 20,
              borderTop: "1px solid var(--glass-border)",
              flexWrap: "wrap",
              gap: 12,
            }}
          >
            {/* Back */}
            <button
              type="button"
              onClick={goBack}
              disabled={filteredSteps.length > 0 ? currentStep === filteredSteps[0].id : true}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 18px",
                borderRadius: 10,
                background: "var(--icon-btn-bg)",
                border: "1px solid var(--glass-border)",
                color: (filteredSteps.length > 0 ? currentStep === filteredSteps[0].id : true)
                  ? "var(--text-muted)"
                  : "var(--text-primary)",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: (filteredSteps.length > 0 ? currentStep === filteredSteps[0].id : true)
                  ? "not-allowed"
                  : "pointer",
                opacity: (filteredSteps.length > 0 ? currentStep === filteredSteps[0].id : true)
                  ? 0.5
                  : 1,
                transition: "all 0.15s",
              }}
            >
              <ArrowLeft size={15} strokeWidth={2.5} /> Back
            </button>

            {/* Step dots */}
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {filteredSteps.map((s) => {
                const done = completedSteps.includes(s.id);
                const active = currentStep === s.id;
                return (
                  <div
                    key={s.id}
                    style={{
                      width: active ? 20 : 7,
                      height: 7,
                      borderRadius: 999,
                      background: done
                        ? s.color
                        : active
                          ? "var(--brand-indigo)"
                          : "var(--glass-border)",
                      transition: "all 0.3s ease",
                    }}
                  />
                );
              })}
            </div>

            {/* Primary action */}
            {((currentStep !== 2 &&
              currentStep !== 6 &&
              currentStep !== 7 &&
              currentStep !== 9 &&
              currentStep !== 10 &&
              currentStep !== 11) ||
              completedSteps.includes(currentStep)) && (
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={btnLoader}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "9px 22px",
                  borderRadius: 10,
                  background:
                    completedSteps.includes(currentStep) && currentStep !== 11
                      ? "var(--icon-btn-bg)"
                      : `linear-gradient(135deg,${currentStepDef.color},${currentStepDef.color}cc)`,
                  border:
                    completedSteps.includes(currentStep) && currentStep !== 11
                      ? "1px solid var(--glass-border)"
                      : "none",
                  color:
                    completedSteps.includes(currentStep) && currentStep !== 11
                      ? "var(--text-primary)"
                      : "#fff",
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  cursor: btnLoader ? "not-allowed" : "pointer",
                  opacity: btnLoader ? 0.7 : 1,
                  boxShadow:
                    completedSteps.includes(currentStep) && currentStep !== 11
                      ? "none"
                      : `0 4px 20px ${currentStepDef.color}40`,
                  transition: "all 0.2s",
                }}
              >
                {btnLoader ? (
                  <>
                    <div
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        border: "2px solid rgba(255,255,255,0.3)",
                        borderTopColor: "#fff",
                        animation: "spin 0.7s linear infinite",
                      }}
                    />
                    Saving…
                  </>
                ) : (
                  <>
                    {primaryLabel()}
                    {filteredSteps.findIndex((s) => s.id === currentStep) <
                    filteredSteps.length - 1 ? (
                      <ArrowRight size={15} strokeWidth={2.5} />
                    ) : (
                      <CheckCircle size={15} strokeWidth={2.5} />
                    )}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Spinner keyframe */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default Onboarding;
